const finiteNumber = (value) => {
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^-?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim()))) {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const readStoredAverages = (stored) => {
  try {
    const averages = typeof stored === 'string' ? JSON.parse(stored) : stored;
    return averages && typeof averages === 'object' && !Array.isArray(averages) ? averages : {};
  } catch (error) {
    return {};
  }
};

export const weightedPrescreenContribution = (aggregate) => {
  if (!aggregate || typeof aggregate !== 'object') {
    return '';
  }
  const average = finiteNumber(aggregate.GroupAverageAsDecimal);
  const weight = finiteNumber(aggregate.Weight);
  if (average == null || weight == null || !Number.isFinite(average * weight)) {
    return '';
  }
  return (average * weight).toFixed(2);
};

export const applicablePrescreenCategories = (categories, isPrescreenEnabled) => {
  if (!isPrescreenEnabled) {
    return [];
  }
  if (!Array.isArray(categories)) {
    throw new Error('Prescreen category configuration is unavailable.');
  }
  return categories.filter((category) => {
    const weight = finiteNumber(category.PrescreenWeight);
    if (category.QuestionType !== 'scale' || category.Deleted === true || category.Deleted === 1 || weight == null || weight <= 0) {
      return false;
    }
    const counts = ['PrescreenQuestionCount', 'PrescreenAdditionalScoreCount', 'PrescreenLoRScoreCount'].map((key) => {
      return finiteNumber(category[key]);
    });
    if (counts.some((count) => { return count == null || count < 0 || !Number.isInteger(count); })) {
      throw new Error('Prescreen category source configuration is unavailable. Refresh before exporting.');
    }
    return counts.some((count) => { return count > 0; });
  });
};

// The caller supplies the selected season's applicable category configuration.
// It must not infer category count from individual candidates' stored scores.
export const appendPrescreenCategoryContributions = ({ rows, candidates, categories, scope }) => {
  if (!Array.isArray(categories)) {
    throw new Error('Prescreen category configuration is unavailable.');
  }
  const orderedCategories = [...categories].sort((a, b) => {
    return Number(a.pk_EvaluationQuestionWeight) - Number(b.pk_EvaluationQuestionWeight);
  });
  const ids = orderedCategories.map((category) => {
    return Number(category.pk_EvaluationQuestionWeight);
  });
  if (ids.some((id) => { return !Number.isSafeInteger(id) || id <= 0; }) || new Set(ids).size !== ids.length) {
    throw new Error('Prescreen category identities are unavailable.');
  }
  if (rows.length !== candidates.length + 1 || candidates.some((candidate) => {
    // Missing metadata is compatible with ordinary exports; known foreign rows
    // are always rejected, and enhanced exports always require provenance.
    if (candidate.PrescreenCSVScope == null) {
      return orderedCategories.length > 1;
    }
    return (
      String(candidate.PrescreenCSVScope.pk_Department) !== String(scope.pk_Department) ||
      String(candidate.PrescreenCSVScope.pk_Season) !== String(scope.pk_Season)
    );
  })) {
    throw new Error('Candidate data changed. Refresh the candidate list before exporting category scores.');
  }
  if (orderedCategories.length <= 1) {
    return rows;
  }
  const categoryNames = orderedCategories.map((category) => {
    return category.CategoryName == null || String(category.CategoryName).trim() === '' ?
      'Unnamed category' : String(category.CategoryName);
  });
  const nameCounts = new Map();
  categoryNames.forEach((name) => { nameCounts.set(name, (nameCounts.get(name) || 0) + 1); });
  // Keep IDs internal. Reserve configured names so numbered duplicates cannot
  // collide with an existing name such as "Research (1)".
  const usedNames = new Set(categoryNames);
  const nextOccurrence = new Map();
  const headers = categoryNames.map((name) => {
    let label = name;
    if (nameCounts.get(name) > 1) {
      let occurrence = nextOccurrence.get(name) || 1;
      do {
        label = `${name} (${occurrence++})`;
      } while (usedNames.has(label));
      nextOccurrence.set(name, occurrence);
      usedNames.add(label);
    }
    // react-csv encloses cells but does not escape quotes. Escape only new
    // headers; existing columns and their protection stay unchanged.
    return `Prescreen weighted contribution: ${label}`.replace(/"/g, '""');
  });
  return [
    [...rows[0], ...headers],
    ...candidates.map((candidate, i) => {
      const averages = readStoredAverages(candidate.StoredPrescreenCandidateScoreAverages);
      const values = orderedCategories.map((category) => {
        return weightedPrescreenContribution(averages[category.pk_EvaluationQuestionWeight]);
      });
      return [...rows[i + 1], ...values];
    }),
  ];
};

export const preparePrescreenCSV = async ({ rows, candidates, scope, isPrescreenEnabled, loadCategories, isCurrentScope }) => {
  if (!isCurrentScope()) {
    throw new Error('The department or season changed. Start the export again.');
  }
  if (!isPrescreenEnabled) {
    return appendPrescreenCategoryContributions({ rows, candidates, categories: [], scope });
  }
  const configured = await loadCategories(scope);
  if (!isCurrentScope()) {
    throw new Error('The department or season changed. Start the export again.');
  }
  const categories = applicablePrescreenCategories(configured, isPrescreenEnabled);
  return appendPrescreenCategoryContributions({ rows, candidates, categories, scope });
};
